import React, { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { Chart, ArcElement, DoughnutController, Tooltip, Legend } from 'chart.js';
import { db, functionsEurope } from '../../firebase';
import { notifySuccess, notifyError } from '../../utils/notify';
import {
  UsersIcon,
  RectangleStackIcon,
  PlayIcon,
  UserPlusIcon,
  BoltIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  TrophyIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

Chart.register(ArcElement, DoughnutController, Tooltip, Legend);

function MetricCard({ icon: Icon, label, value, subtext }) {
  return (
    <div className="gch-card px-5 py-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-white/5">
          <Icon className="w-5 h-5 text-[var(--admin-accent)]" />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-bold gch-text-primary">{value ?? '\u2014'}</p>
          <p className="text-xs gch-text-secondary">{label}</p>
          {subtext && <p className="text-[10px] gch-text-muted mt-0.5">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}

function TrendColumn({ label, value }) {
  return (
    <div className="flex-1 text-center">
      <p className="text-3xl font-bold gch-text-primary">{value ?? '\u2014'}</p>
      <p className="text-xs gch-text-secondary mt-1">{label}</p>
    </div>
  );
}

const fmt = (v) => v != null ? v.toLocaleString() : '\u2014';

const PIE_COLORS = [
  '#9B59B6', '#3498DB', '#16A34A', '#E56A54', '#F4B942',
  '#06B6D4', '#EC4899', '#8B5CF6', '#F97316', '#64748B',
];

function GameTypePieChart({ gameTypes }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || gameTypes.length === 0) return;

    if (chartRef.current) chartRef.current.destroy();

    const labels = gameTypes.map(([type]) => type.charAt(0).toUpperCase() + type.slice(1));
    const data = gameTypes.map(([, count]) => count);

    chartRef.current = new Chart(canvasRef.current, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: PIE_COLORS.slice(0, gameTypes.length),
          borderColor: 'rgba(46, 61, 74, 0.8)',
          borderWidth: 2,
          hoverBorderColor: '#fff',
          hoverBorderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '55%',
        plugins: {
          legend: {
            position: 'right',
            labels: {
              color: '#B8C5D0',
              font: { size: 11, family: 'Inter' },
              padding: 12,
              usePointStyle: true,
              pointStyleWidth: 8,
            },
          },
          tooltip: {
            backgroundColor: '#2E3D4A',
            titleColor: '#fff',
            bodyColor: '#B8C5D0',
            borderColor: 'rgba(255,255,255,0.1)',
            borderWidth: 1,
            padding: 10,
            callbacks: {
              label: (ctx) => ` ${ctx.label}: ${ctx.parsed} game${ctx.parsed !== 1 ? 's' : ''}`,
            },
          },
        },
      },
    });

    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [gameTypes]);

  return <canvas ref={canvasRef} />;
}

export default function AdminOverview() {
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [campaignOwners, setCampaignOwners] = useState({}); // campaignId -> workspace name

  const fetchData = useCallback(async () => {
    try {
      const [userRes, campaignRes] = await Promise.all([
        httpsCallable(functionsEurope, 'getUserAnalytics')(),
        httpsCallable(functionsEurope, 'getCampaignAnalytics')({ period: 'alltime' }),
      ]);
      setUserAnalytics(userRes.data);
      setCampaignAnalytics(campaignRes.data);
      setLastUpdated(new Date());

      // Resolve workspace names for top campaigns
      const allPopular = [
        ...(campaignRes.data?.studio?.popularCampaigns || []),
        ...(campaignRes.data?.arcade?.popularCampaigns || []),
      ].sort((a, b) => (b.plays || 0) - (a.plays || 0)).slice(0, 5);

      if (allPopular.length > 0) {
        const owners = {};
        await Promise.all(allPopular.map(async (c) => {
          try {
            const campaignDoc = await getDoc(doc(db, 'campaign', c.id));
            if (!campaignDoc.exists()) return;
            const companyId = campaignDoc.data().companyId;
            if (!companyId) return;
            const wsDoc = await getDoc(doc(db, 'workspace', companyId));
            owners[c.id] = wsDoc.exists() ? (wsDoc.data().companyName || wsDoc.data().name || companyId) : companyId;
          } catch { /* ignore */ }
        }));
        setCampaignOwners(owners);
      }
    } catch (error) {
      console.error('Failed to load admin analytics:', error);
      notifyError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await httpsCallable(functionsEurope, 'refreshAnalyticsCache')();
      await fetchData();
      notifySuccess('Analytics refreshed');
    } catch (error) {
      notifyError('Failed to refresh analytics');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="gch-card px-5 py-4 h-20 animate-pulse bg-white/5 rounded-xl" />
          ))}
        </div>
        <div className="gch-card h-24 animate-pulse bg-white/5 rounded-xl" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="gch-card h-48 animate-pulse bg-white/5 rounded-xl" />
          <div className="gch-card h-48 animate-pulse bg-white/5 rounded-xl" />
        </div>
      </div>
    );
  }

  const ov = userAnalytics?.overview || {};
  const trend = userAnalytics?.registrationTrend || {};

  // Merge studio + arcade popular campaigns, sort by plays, take top 5
  const studioPopular = campaignAnalytics?.studio?.popularCampaigns || [];
  const arcadePopular = campaignAnalytics?.arcade?.popularCampaigns || [];
  const topCampaigns = [...studioPopular, ...arcadePopular]
    .sort((a, b) => (b.plays || 0) - (a.plays || 0))
    .slice(0, 5);

  // Merge game types from studio + arcade
  const typeMap = {};
  for (const t of (campaignAnalytics?.studio?.gameTypes || [])) {
    typeMap[t.type] = (typeMap[t.type] || 0) + t.createdCount;
  }
  for (const t of (campaignAnalytics?.arcade?.gameTypes || [])) {
    typeMap[t.type] = (typeMap[t.type] || 0) + t.createdCount;
  }
  const gameTypes = Object.entries(typeMap)
    .sort(([, a], [, b]) => b - a);

  const totalPlays = (campaignAnalytics?.studio?.totalPlays || 0) + (campaignAnalytics?.arcade?.totalPlays || 0);
  const totalCampaigns = (campaignAnalytics?.studio?.totalCampaigns || 0) + (campaignAnalytics?.arcade?.totalCampaigns || 0);

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard
          icon={UsersIcon}
          label="Total Users"
          value={fmt(ov.totalUsers)}
          subtext={ov.usersToday ? `+${ov.usersToday} today` : undefined}
        />
        <MetricCard
          icon={BoltIcon}
          label="Active Users (7d)"
          value={fmt(ov.activeUsers)}
        />
        <MetricCard
          icon={UserPlusIcon}
          label="Signups (7d)"
          value={fmt(ov.usersLast7Days)}
          subtext={ov.growthRate ? `${ov.growthRate > 0 ? '+' : ''}${Number(ov.growthRate).toFixed(0)}% growth` : undefined}
        />
        <MetricCard
          icon={RectangleStackIcon}
          label="Games Created"
          value={fmt(totalCampaigns)}
        />
        <MetricCard
          icon={PlayIcon}
          label="Total Plays"
          value={fmt(totalPlays)}
        />
      </div>

      {/* Registration trend */}
      <div className="gch-card px-6 py-5">
        <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary mb-4">New Signups</p>
        <div className="flex divide-x divide-white/10">
          <TrendColumn label="Today" value={fmt(trend.today)} />
          <TrendColumn label="Last 7 days" value={fmt(trend.last7Days)} />
          <TrendColumn label="Last 30 days" value={fmt(trend.last30Days)} />
        </div>
      </div>

      {/* Bottom two cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top campaigns */}
        <div className="gch-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
            <TrophyIcon className="w-4 h-4 text-[var(--admin-accent)]" />
            <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary">Top Campaigns</p>
          </div>
          {topCampaigns.length === 0 ? (
            <p className="px-5 py-8 text-sm gch-text-muted text-center">No campaign data yet</p>
          ) : (
            <div className="divide-y divide-[var(--admin-border)]">
              {topCampaigns.map((c, i) => (
                <div key={c.id} className="group flex items-center gap-3 px-5 py-2.5 hover:bg-white/[0.03] transition-colors">
                  <span className="text-xs font-bold gch-text-muted w-5 text-right">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm gch-text-primary truncate">{c.name || 'Untitled'}</p>
                    <p className="text-[10px] gch-text-muted truncate">
                      <span className="capitalize">{c.gameType}</span>
                      {campaignOwners[c.id] && <span> &middot; {campaignOwners[c.id]}</span>}
                    </p>
                  </div>
                  <p className="text-sm font-medium gch-text-secondary tabular-nums">{fmt(c.plays)}</p>
                  <a
                    href={`/p/${c.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 rounded-lg hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                    title="Play game"
                  >
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 gch-text-secondary" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Game type breakdown */}
        <div className="gch-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
            <Squares2X2Icon className="w-4 h-4 text-[var(--admin-accent)]" />
            <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary">Game Types</p>
          </div>
          {gameTypes.length === 0 ? (
            <p className="px-5 py-8 text-sm gch-text-muted text-center">No game data yet</p>
          ) : (
            <div className="px-5 py-4" style={{ height: 220 }}>
              <GameTypePieChart gameTypes={gameTypes} />
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        {lastUpdated && (
          <p className="text-xs gch-text-muted">
            Last updated: {lastUpdated.toLocaleString()}
          </p>
        )}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium gch-text-secondary hover:text-white transition-colors disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}
